import { Injectable, Injector, inject } from '@angular/core';
import { MtmAuthBridgeService } from '@mtm/auth-ui-angular';
import { Observable, defer, firstValueFrom } from 'rxjs';

/**
 * - `renewed`: a fresh app token is stored, the request can be replayed.
 * - `session-invalid`: IAM answered 401 — the session is really gone, logging out is right.
 * - `unavailable`: renewal failed for any other reason (network, 429, 5xx, lock) — the session may
 *   still be valid, so the user must not be logged out for it.
 */
export type AppTokenRenewalOutcome = 'renewed' | 'session-invalid' | 'unavailable';

/**
 * Renews the IAM app token on demand, after a gateway 401. Concurrent callers share one in-flight
 * renewal (the SDK also serialises renewals across tabs with a Web Lock), so a burst of 401s —
 * e.g. a flood of notification refetches — triggers a single renewal.
 */
@Injectable({
  providedIn: 'root',
})
export class AppTokenRenewalService {
  // Resolved on first renewal, not at construction: this service is injected by AppHttpInterceptor and
  // must not pull the IAM SDK into the HTTP interceptor chain while HttpClient is being built.
  private readonly injector = inject(Injector);
  private inFlight: Promise<AppTokenRenewalOutcome> | null = null;

  public renew(): Observable<AppTokenRenewalOutcome> {
    return defer(() => {
      if (!this.inFlight) {
        this.inFlight = firstValueFrom(this.injector.get(MtmAuthBridgeService).renewAppToken$())
          .then((): AppTokenRenewalOutcome => 'renewed')
          .catch((err: { status?: number }): AppTokenRenewalOutcome => {
            console.warn('App token renewal failed', err);
            return err?.status === 401 ? 'session-invalid' : 'unavailable';
          })
          .finally(() => {
            this.inFlight = null;
          });
      }
      return this.inFlight;
    });
  }
}
